<template>
  <LayoutCard title="Expiration Policy">
    <template #description>
      Choose when licenses expire. Saving recalculates expiration dates for existing licenses with a valid issue date,
      then refreshes property violation statuses for this municipality.
    </template>

    <div v-if="saveError" class="alert alert-danger mb-0" role="alert">{{ saveError }}</div>
    <div v-if="needsMigration" class="alert alert-info mb-0">
      This municipality uses the older March 1 policy. Save to apply the annual period shown below
      and recalculate existing license dates.
    </div>

    <fieldset :disabled="saving" :aria-busy="saving">
      <legend class="visually-hidden">License expiration settings</legend>
      <div class="mb-3">
        <label :for="`${formId}-type`" class="form-label">Expiration policy</label>
        <select :id="`${formId}-type`" v-model="form.type" class="form-select" data-test="policy">
          <option value="" disabled>Choose an expiration policy</option>
          <option value="annual-date">Annual license period</option>
          <option value="one-year">One year from issuance</option>
        </select>
      </div>

      <template v-if="isAnnual">
        <div class="row g-3 mb-3">
          <div class="col-sm-8">
            <label :for="`${formId}-month`" class="form-label">Period start month</label>
            <select
              :id="`${formId}-month`"
              v-model="form.month"
              class="form-select"
              data-test="month"
              :aria-invalid="invalidMonthDay"
              :aria-describedby="`${formId}-date-help ${formId}-date-error`">
              <option value="" disabled>Choose a month</option>
              <option v-for="month in months" :key="month.value" :value="month.value">{{ month.label }}</option>
            </select>
          </div>
          <div class="col-sm-4">
            <label :for="`${formId}-day`" class="form-label">Period start day</label>
            <select
              :id="`${formId}-day`"
              v-model="form.day"
              class="form-select"
              :class="{ 'is-invalid': invalidMonthDay }"
              data-test="day"
              :aria-invalid="invalidMonthDay"
              :aria-describedby="`${formId}-date-help ${formId}-date-error`">
              <option value="" disabled>Choose a day</option>
              <option v-for="day in days" :key="day" :value="day" :disabled="Number(day) > daysInMonth">
                {{ Number(day) }}
              </option>
            </select>
          </div>
          <div class="col-12 mt-1">
            <div :id="`${formId}-date-help`" class="form-text">
              The selected date starts each annual license period. Licenses expire on the day before the next period starts.
              January 1 gives a December 31 expiration; March 1 gives February 28 or 29.
              <span v-if="monthDay === '02-29'" class="d-block">A February 29 period start uses February 28 in non-leap years.</span>
            </div>
            <div v-if="invalidMonthDay" :id="`${formId}-date-error`" class="text-danger small" role="alert">
              Choose a valid month and day.
            </div>
          </div>
        </div>

        <label :for="`${formId}-allowance`" class="form-label">Early-issuance allowance (months)</label>
        <input
          :id="`${formId}-allowance`"
          v-model.number="form.allowance"
          type="number"
          min="0"
          max="11"
          step="1"
          class="form-control"
          :class="{ 'is-invalid': invalidAllowance }"
          data-test="allowance"
          :aria-invalid="invalidAllowance"
          :aria-describedby="`${formId}-allowance-help ${formId}-allowance-error`" />
        <div :id="`${formId}-allowance-help`" class="form-text">
          Licenses issued within this many calendar months before the period starts expire one annual cycle later.
          Use 0 for no early issuance. For a March 1 period start, 2 lets January and February issues remain valid through the last day of February in the next year.
        </div>
        <div v-if="invalidAllowance" :id="`${formId}-allowance-error`" class="invalid-feedback" role="alert">
          Enter a whole number from 0 to 11.
        </div>

        <div class="mt-3">
          <label :for="`${formId}-first-period`" class="form-label">First license period starts on (optional)</label>
          <input
            :id="`${formId}-first-period`"
            v-model="form.firstPeriodStartsOn"
            type="date"
            class="form-control"
            :class="{ 'is-invalid': invalidFirstPeriod }"
            data-test="first-period"
            :aria-invalid="invalidFirstPeriod"
            :aria-describedby="`${formId}-first-period-help ${formId}-first-period-error`" />
          <div :id="`${formId}-first-period-help`" class="form-text">
            For a new program, licenses issued before this date use the first annual period.
            Actual application and issue dates are preserved. The date must match the annual period start above.
            Keep this setting after launch so recalculations preserve the initial licenses;
            the early-issuance allowance still applies in future years. Leave blank for an established program.
          </div>
          <div v-if="invalidFirstPeriod" :id="`${formId}-first-period-error`" class="invalid-feedback" role="alert">
            Enter a valid date matching the annual period start month and day.
          </div>
        </div>
      </template>
      <div v-else-if="form.type === 'one-year'" class="form-text">
        Licenses expire on the day before the first anniversary of their issue date, which is their last valid day.
        Licenses issued February 29 remain valid through February 28 the following year; their next period starts March 1.
      </div>
    </fieldset>

    <template v-if="changed" #footer>
      <span v-if="saving" class="me-3 small" role="status">Saving, recalculating license dates, and refreshing violations…</span>
      <button type="button" class="btn btn-flat ms-1" :disabled="saving" @click="cancel">Cancel</button>
      <button type="button" class="btn btn-primary ms-1" :disabled="saving || invalid" @click="commit">
        Save Changes
      </button>
    </template>
  </LayoutCard>
</template>

<script>
import LayoutCard from '@/components/common/LayoutCard.vue';
import { City } from '@/models/City';
import { normalizePeriodStartMonthDay, resolveLicensePeriodStartMonthDay, normalizeFirstLicensePeriodStartsOn } from '@strenforcement/domain/licenseExpiration';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December']
  .map((label, index) => ({ label, value: String(index + 1).padStart(2, '0') }));
const DAYS = Array.from({ length: 31 }, (_, index) => String(index + 1).padStart(2, '0'));
let nextFormId = 0;

export default {
  components: { LayoutCard },
  props: { city: City },
  data() {
    return {
      formId: `license-expiration-${++nextFormId}`,
      months: MONTHS,
      days: DAYS,
      form: {},
      savedForm: {},
      needsMigration: false,
      saving: false,
      saveError: ''
    };
  },
  watch: {
    city: {
      immediate: true,
      handler(city, previousCity) {
        if (!this.saving || city?.id !== previousCity?.id) this.reset();
      }
    }
  },
  computed: {
    isAnnual() {
      return this.form.type === 'annual-date';
    },
    monthDay() {
      return `${this.form.month}-${this.form.day}`;
    },
    daysInMonth() {
      // Leap year permits February 29; the shared calculation handles other years.
      return new Date(2000, Number(this.form.month), 0).getDate();
    },
    invalidMonthDay() {
      try {
        normalizePeriodStartMonthDay(this.monthDay);
        return false;
      } catch {
        return true;
      }
    },
    invalidAllowance() {
      return !Number.isInteger(this.form.allowance) || this.form.allowance < 0 || this.form.allowance > 11;
    },
    invalidFirstPeriod() {
      try {
        normalizeFirstLicensePeriodStartsOn(this.form.firstPeriodStartsOn || null, this.monthDay);
        return false;
      } catch {
        return true;
      }
    },
    invalid() {
      return !['annual-date', 'one-year'].includes(this.form.type)
        || (this.isAnnual && (this.invalidMonthDay || this.invalidAllowance || this.invalidFirstPeriod));
    },
    changed() {
      return this.needsMigration || this.form.type !== this.savedForm.type
        || (this.isAnnual && (this.form.month !== this.savedForm.month
          || this.form.day !== this.savedForm.day || this.form.allowance !== this.savedForm.allowance
          || this.form.firstPeriodStartsOn !== this.savedForm.firstPeriodStartsOn));
    }
  },
  methods: {
    cancel() {
      this.form = { ...this.savedForm };
      this.saveError = '';
    },
    reset() {
      const type = (this.city?.expirationType || 'end-of-year').replace(/_/g, '-').toLowerCase();
      this.needsMigration = type === 'march-first';
      const policy = this.city?.data || {};
      const hasPeriodStart = policy.licensePeriodStartMonthDay !== undefined || policy.licenseExpirationMonthDay !== undefined;
      const cutoff = hasPeriodStart ? resolveLicensePeriodStartMonthDay(policy)
        : this.needsMigration ? '03-01' : this.city?.licensePeriodStartMonthDay ?? '01-01';
      const [month = '', day = ''] = String(cutoff).split('-');
      this.form = {
        type: ['annual-date', 'end-of-year', 'march-first'].includes(type) ? 'annual-date' : type === 'one-year' ? 'one-year' : '',
        month,
        day,
        firstPeriodStartsOn: policy.licenseFirstPeriodStartsOn ?? '',
        allowance: this.city?.licenseIssuedEarlyAllowanceMonths ?? 1
      };
      this.savedForm = { ...this.form };
      this.saveError = '';
    },
    async commit() {
      if (this.saving || this.invalid || !this.changed) return;
      const city = this.city;
      const selected = { ...this.form };
      const policy = { licenseExpirationType: selected.type };
      if (this.isAnnual) {
        policy.licensePeriodStartMonthDay = this.monthDay;
        policy.licenseIssuedEarlyAllowanceMonths = selected.allowance;
        if (selected.firstPeriodStartsOn || this.savedForm.firstPeriodStartsOn) {
          policy.licenseFirstPeriodStartsOn = selected.firstPeriodStartsOn || null;
        }
      }
      this.saving = true;
      this.saveError = '';
      try {
        await city.updateLicenseExpirationPolicy(policy);
        if (this.city?.id !== city.id) return;
        // A city snapshot may refresh while the request is pending. Keep the saved selection.
        this.form = selected;
        this.savedForm = { ...selected };
        this.needsMigration = false;
        this.$showToast('Expiration policy saved; license dates and violations refreshed');
      } catch {
        if (this.city?.id === city.id) {
          this.saveError = 'Could not finish saving the policy, recalculating license dates, or refreshing violations. Please try again.';
        }
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>
